<template>
    <div class="user-list">
        <div class="user-list-table">
            <Table
                :columns="columns"
                :data="tableData"
                :row-class-name="rowClassName"
                no-data-text="No results. Please check your filters."
            >
                <template #username="{ row }">
                    <template v-if="row.search">
                        <Input
                            :model-value="userFilter"
                            size="small"
                            placeholder="search"
                            search
                            clearable
                            data-testid="users-list__username"
                            @onChange="onUserFilterChange"
                        />
                    </template>

                    <user-role-list-name v-else :user="row"></user-role-list-name>
                </template>

                <template #role="{ row }">
                    <template v-if="row.search">
                        <Input
                            :model-value="roleFilter"
                            size="small"
                            placeholder="search"
                            search
                            clearable
                            data-testid="users-list__role"
                            @onChange="onRoleFilterChange"
                        />
                    </template>

                    <UserRoleSelect
                        v-else
                        :disabled="isRoleDisabled(row)"
                        transfer
                        :title="row.id"
                        :model-value="row.role._id"
                        @update:modelValue="onRoleChange(row.id, $event)"
                    />
                </template>

                <template #brand="{ row }">
                    <template v-if="row.search">
                        <Input
                            :model-value="brandFilter"
                            size="small"
                            placeholder="search"
                            search
                            clearable
                            data-testid="users-list__brand"
                            @onChange="onBrandFilterChange"
                        />
                    </template>

                    <template v-else>
                        <template v-if="getUserTableData(row).length">{{ getClientsString(row) }}</template>

                        <div v-else class="user-list-table_empty-wrapper" @click.capture.stop="showModal(row)">
                            None
                        </div>
                    </template>
                </template>

                <template #action="{ row }">
                    <Button
                        v-if="!row.search"
                        class="user-list-table_action-btn"
                        size="small"
                        data-testid="users-list__edit-button"
                        @click="showModal(row)"
                    >
                        Edit
                    </Button>
                </template>
            </Table>

            <Page
                v-if="filteredUsers.length"
                class="paging-control paging-control-pages user-list-table__pages"
                :model-value="page"
                :total="filteredUsers.length"
                :page-size="pageSize"
                @onChange="setPage"
            />
        </div>

        <UserAccessScopeModal
            v-if="isModalVisible"
            :user="modalUserData"
            @userDeleted="onUserDeleted"
            @close="hideModal"
        />
    </div>
</template>

<script>
import { defineComponent } from "vue";
import UserRoleSelect from "@/components/ManageUsers/UserRoleSelect.vue";
import UserAccessScopeModal from "@/components/ManageUsers/UserAccessScopeModal.vue";
import UserRoleListName from "@/components/ManageUsers/UserRoleListName.vue";
import { AuthGetters } from "@/store/modules/auth";
import { usePagination } from "@/composables/usePagination";

export default defineComponent({
    name: "UserRolesList",
    components: {
        UserRoleSelect,
        UserRoleListName,
        UserAccessScopeModal
    },

    props: {
        users: {
            type: Array,
            default() {
                return [];
            }
        }
    },

    emits: ["userDeleted", "roleChanged"],

    setup() {
        const { page, pageSize, setPage, currentPageItems, setPaginationData } = usePagination({
            pageSize: 6
        });

        return {
            page,
            setPage,
            pageSize,
            currentPageItems,
            setPaginationData
        };
    },

    data() {
        return {
            columns: [
                {
                    title: "User",
                    slot: "username"
                },

                {
                    title: "Role",
                    width: 200,
                    slot: "role"
                },

                {
                    title: "Brand",
                    width: 500,
                    slot: "brand"
                },

                {
                    title: "Action",
                    slot: "action",
                    key: "action",
                    width: 85,
                    align: "center"
                }
            ],

            isModalVisible: false,
            modalUserData: null,
            userFilter: "",
            roleFilter: "",
            brandFilter: ""
        };
    },

    computed: {
        sortedUsers() {
            return [...this.users].sort((a, b) => {
                const idA = a.id.toLowerCase();
                const idB = b.id.toLowerCase();
                if (idA === idB) {
                    return 0;
                }

                return idA < idB ? -1 : 1;
            });
        },

        filteredUsers() {
            let filtered = this.sortedUsers;
            if (this.userFilter) {
                filtered = filtered.filter(u => u.id.indexOf(this.userFilter) > -1);
            }

            if (this.roleFilter) {
                filtered = filtered.filter(u => u.role.name && u.role.name.toLowerCase().indexOf(this.roleFilter) > -1);
            }

            if (this.brandFilter) {
                filtered = filtered.filter(u => {
                    const matchedClient =
                        u.scope.client && u.scope.client.some(c => c.name.toLowerCase().indexOf(this.brandFilter) > -1);

                    if (matchedClient) {
                        return true;
                    }

                    const matchedCampaign =
                        u.scope.campaign &&
                        u.scope.campaign.some(c => c.name.toLowerCase().indexOf(this.brandFilter) > -1);

                    if (matchedCampaign) {
                        return true;
                    }

                    const matchedCampaignOnlyClient =
                        u.scope.campaignOnlyClient &&
                        u.scope.campaignOnlyClient.some(c => c.name.toLowerCase().indexOf(this.brandFilter) > -1);

                    if (matchedCampaignOnlyClient) {
                        return true;
                    }

                    return false;
                });
            }

            return filtered;
        },

        tableData() {
            return [{ search: true }].concat(this.currentPageItems);
        },

        userId() {
            return this.$store.state.auth.me && this.$store.state.auth.me.id;
        }
    },

    watch: {
        filteredUsers: {
            handler() {
                this.setPaginationData(this.filteredUsers);
            },
            immediate: true
        }
    },

    methods: {
        getClientsString(user) {
            return Object.values(this.getScopeClients(user))
                .map(c => c.name)
                .join(", ");
        },

        getScopeClients(user) {
            let scopeClients = {};
            let scopeCampaign = {};

            if (user.scope.client) {
                scopeClients = user.scope.client.reduce((acc, cur) => {
                    if (this.$auth.hasScope({ clientId: cur._id })) {
                        return Object.assign(acc, { [cur._id]: cur });
                    }

                    return acc;
                }, {});
            }

            if (user.scope.campaign) {
                scopeCampaign = user.scope.campaign.reduce((acc, cur) => {
                    const clnt = user.scope.campaignOnlyClient.find(cl => cl.id === cur.clientId);

                    if (
                        clnt !== undefined &&
                        (this.$auth.hasScope({ clientId: clnt._id }) ||
                            this.$auth.hasScope({
                                clientId: clnt._id,
                                campaignId: cur._id
                            }))
                    ) {
                        return Object.assign(acc, { [clnt._id]: clnt });
                    }

                    return acc;
                }, {});
            }

            return {
                ...scopeClients,
                ...scopeCampaign
            };
        },

        getUserTableData(user) {
            const scopedClients = this.getScopeClients(user);
            return Object.keys(scopedClients).map(clientId => scopedClients[clientId]);
        },

        hideModal() {
            this.isModalVisible = false;
        },

        isRoleDisabled({ id }) {
            if (this.isSuperAdmin()) {
                return false;
            }

            return this.userId === id;
        },

        isSuperAdmin() {
            return this.$store.getters[AuthGetters.isSuperAdmin];
        },

        onBrandFilterChange(event) {
            this.brandFilter = event.target.value.toLowerCase();
            this.setPage(1);
        },

        onRoleFilterChange(event) {
            this.roleFilter = event.target.value.toLowerCase();
            this.setPage(1);
        },

        onUserFilterChange(event) {
            this.userFilter = event.target.value.toLowerCase();
            this.setPage(1);
        },

        onUserDeleted() {
            this.$emit("userDeleted");
        },

        onRoleChange(userId, roleId) {
            this.$emit("roleChanged", {
                roleId,
                userId
            });
        },

        rowClassName(row) {
            return row.search ? "search-row" : "";
        },

        showModal(userData) {
            this.modalUserData = userData;
            this.isModalVisible = true;
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.user-list {
    margin-top: 28px;

    &-table {
        .ivu-table td {
            vertical-align: top;
            padding-top: 16px;
            padding-bottom: 12px;

            .ivu-table {
                background-color: inherit;

                td.bordered-column > .ivu-table-cell {
                    background-color: $white;
                }

                td {
                    vertical-align: middle;
                    padding-top: 0;
                    padding-bottom: 0;
                    background-color: inherit;
                }

                .ivu-icon.ivu-select-arrow {
                    top: 12px;
                }
            }
        }

        .ivu-table .search-row td {
            padding-top: 6px;
            padding-bottom: 6px;
            height: 36px;
        }

        .ivu-select-single .ivu-select-selection {
            height: 34px;
        }

        .add-user-name {
            width: 300px;
            padding: 17px 0;
        }

        .new-user-row {
            td {
                background-color: $grey1;
            }
        }

        &-more {
            color: $blue;

            &:hover {
                background-color: inherit;
            }
        }

        &-confirm {
            margin-left: 10px;
        }

        &_empty-wrapper {
            .user-permission-modal-table-wrapper {
                pointer-events: none;
            }
        }

        &_action-btn {
            display: none;
        }

        .ivu-table-row:hover &_action-btn {
            display: block;
        }

        &__pages {
            float: right;
            margin-top: 16px;
        }
    }

    &-add {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 16px;
    }
}
</style>
